/**
 * Group Links API Slice
 * RTK Query endpoints for group linking (duplicate detection & contact sharing)
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();

export const groupLinksApi = createApi({
  reducerPath: 'groupLinksApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: ['GroupLinks', 'SimilarGroups', 'PendingLinks', 'Stats'],
  endpoints: (builder) => ({
    // Scan for similar groups
    scanSimilarGroups: builder.query({
      query: ({ threshold = 0.75, limit = 5 } = {}) => ({
        url: 'fleet/group-links/scan/',
        params: { threshold, limit },
      }),
      providesTags: ['SimilarGroups'],
    }),

    // Get all group links
    getGroupLinks: builder.query({
      query: (status) => ({
        url: 'fleet/group-links/',
        params: status ? { status } : {},
      }),
      providesTags: ['GroupLinks'],
    }),

    // Get pending link suggestions
    getPendingLinks: builder.query({
      query: () => 'fleet/group-links/pending/',
      providesTags: ['PendingLinks'],
    }),

    // Get link statistics
    getGroupLinkStats: builder.query({
      query: () => 'fleet/group-links/stats/',
      providesTags: ['Stats'],
    }),

    // Create a new group link
    createGroupLink: builder.mutation({
      query: ({ primary_group_id, linked_group_id }) => ({
        url: 'fleet/group-links/',
        method: 'POST',
        body: { primary_group_id, linked_group_id },
      }),
      invalidatesTags: ['GroupLinks', 'SimilarGroups', 'PendingLinks', 'Stats'],
    }),

    // Confirm a group link
    confirmGroupLink: builder.mutation({
      query: (linkId) => ({
        url: `fleet/group-links/${linkId}/`,
        method: 'PATCH',
        body: { status: 'confirmed' },
      }),
      invalidatesTags: ['GroupLinks', 'PendingLinks', 'Stats'],
    }),

    // Reject a group link
    rejectGroupLink: builder.mutation({
      query: (linkId) => ({
        url: `fleet/group-links/${linkId}/`,
        method: 'PATCH',
        body: { status: 'rejected' },
      }),
      invalidatesTags: ['GroupLinks', 'PendingLinks', 'Stats'],
    }),

    // Delete a group link
    deleteGroupLink: builder.mutation({
      query: (linkId) => ({
        url: `fleet/group-links/${linkId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['GroupLinks', 'SimilarGroups', 'Stats'],
    }),

    // Bulk confirm links
    bulkConfirmLinks: builder.mutation({
      query: (linkIds) => ({
        url: 'fleet/group-links/bulk-confirm/',
        method: 'POST',
        body: { link_ids: linkIds },
      }),
      invalidatesTags: ['GroupLinks', 'PendingLinks', 'Stats'],
    }),

    // Bulk reject links
    bulkRejectLinks: builder.mutation({
      query: (linkIds) => ({
        url: 'fleet/group-links/bulk-reject/',
        method: 'POST',
        body: { link_ids: linkIds },
      }),
      invalidatesTags: ['GroupLinks', 'PendingLinks', 'Stats'],
    }),

    // Search groups by name (for manual linking)
    searchGroups: builder.query({
      query: ({ q, limit = 20 }) => ({
        url: 'fleet/group-links/search/',
        params: { q, limit },
      }),
    }),

    // Get contacts for a group (preview)
    getGroupContacts: builder.query({
      query: (groupId) => `fleet/groups/${groupId}/contacts/`,
    }),

    // Manual link two groups
    manualLinkGroups: builder.mutation({
      query: ({ primary_group_id, linked_group_id }) => ({
        url: 'fleet/group-links/manual-link/',
        method: 'POST',
        body: { primary_group_id, linked_group_id },
      }),
      invalidatesTags: ['GroupLinks', 'SimilarGroups', 'PendingLinks', 'Stats'],
    }),

    // Dismiss a scan result (won't appear in future scans)
    dismissScanResult: builder.mutation({
      query: ({ primary_group_id, linked_group_id }) => ({
        url: 'fleet/group-links/dismiss/',
        method: 'POST',
        body: { primary_group_id, linked_group_id },
      }),
      invalidatesTags: ['SimilarGroups', 'Stats'],
    }),

    // Clear all dismissed pairs (they will appear in scans again)
    clearDismissedLinks: builder.mutation({
      query: () => ({
        url: 'fleet/group-links/clear-dismissed/',
        method: 'POST',
      }),
      invalidatesTags: ['SimilarGroups', 'Stats'],
    }),
  }),
});

export const {
  useScanSimilarGroupsQuery,
  useLazyScanSimilarGroupsQuery,
  useGetGroupLinksQuery,
  useGetPendingLinksQuery,
  useGetGroupLinkStatsQuery,
  useCreateGroupLinkMutation,
  useConfirmGroupLinkMutation,
  useRejectGroupLinkMutation,
  useDeleteGroupLinkMutation,
  useBulkConfirmLinksMutation,
  useBulkRejectLinksMutation,
  useLazySearchGroupsQuery,
  useLazyGetGroupContactsQuery,
  useManualLinkGroupsMutation,
  useDismissScanResultMutation,
  useClearDismissedLinksMutation,
} = groupLinksApi;

export default groupLinksApi;
